import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { observer } from 'mobx-react';
import { Modal, Button } from '@ann/web-components';
import { ModalContent, ActionsContainer } from './ShareKnowledgeModal.styled';
import ShareKnowledgeModalContent from './ShareKnowledgeModalContent';
import PendingUsersList from './PendingUsersList';
import CurrentUsersList from './CurrentUsersList';
import { useMediaQueries } from '../../../../../utils/mediaQueries';
import { useStore } from '../../../../../hooks';
import { useToast } from '../../../../../components/toast';
import { useShareKnowledgeModal } from './hooks/useShareKnowledgeModal';

const MOCK_PRIVILEGES = {
  knowledgeBasePrivileges: [
    { entityPrivilegeUuid: '1', privilegeName: 'Can view' },
    { entityPrivilegeUuid: '2', privilegeName: 'Can edit' },
    { entityPrivilegeUuid: '3', privilegeName: 'Can suggest' },
  ],
};

const DEFAULT_PERMISSION = 'Can edit';

const ShareKnowledgeModalStandalone = observer(({
  isOpen,
  onClose,
  knowledgeBase,
  onShare,
}) => {
  const { usersStore } = useStore();
  const toast = useToast();
  
  // Extract knowledge base UUID
  const knowledgeBaseUuid = useMemo(() => {
    return knowledgeBase?.knowledgebase_uuid 
      || knowledgeBase?.knowledgebaseUuid 
      || knowledgeBase?.id 
      || knowledgeBase?.uuid;
  }, [knowledgeBase?.knowledgebase_uuid, knowledgeBase?.knowledgebaseUuid, knowledgeBase?.id, knowledgeBase?.uuid]);

  // Use the standalone hook
  const {
    currentUsers,
    loading: membersLoading,
    handleUpdateCurrentPermission,
    handleRemoveCurrent,
    handleAddMembers,
  } = useShareKnowledgeModal(knowledgeBaseUuid, isOpen);

  const [selectedUsers, setSelectedUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [isLoadingSharing, setIsLoadingSharing] = useState(false);

  // Load users from UsersStore
  useEffect(() => {
    if (!isOpen) return;
    
    if (usersStore.users.length === 0) {
      usersStore.getUserList({
        page: 1,
        page_size: 100,
        order_by: "fullName",
        order_direction: "ASC",
      }).catch((error) => {
        console.error('Error loading users from UsersStore:', error);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, usersStore.users.length]);

  // Format users from store for Autocomplete
  const usersList = useMemo(() => {
    if (!usersStore.users || !Array.isArray(usersStore.users)) {
      return [];
    }
    return usersStore.users.map((user) => ({
      user_uuid: user.user_uuid || user.userUuid || user.uuid,
      full_name: user.fullName || user.full_name || "Unknown",
      email: user.email || "",
    }));
  }, [usersStore.users]);

  const handleUsersChange = useCallback((newSelectedUsers) => {
    setSelectedUsers((prevSelectedUsers) => {
      const previousUserIds = new Set(prevSelectedUsers.map(u => u.user_uuid));
      const newlyAddedUsers = newSelectedUsers.filter(
        user => !previousUserIds.has(user.user_uuid)
      );

      if (newlyAddedUsers.length > 0) {
        const newPendingUsers = newlyAddedUsers.map((user) => ({
          grantee_uuid: user.user_uuid,
          grantee_name: user.full_name,
          grantee_email: user.email || '',
          privileges: [{ privilege_name: DEFAULT_PERMISSION }],
        }));

        setPendingUsers((prevPending) => [...prevPending, ...newPendingUsers]);
      }

      return [];
    });
  }, []);

  const handleUpdatePendingPermission = useCallback((userId, newPermission) => {
    setPendingUsers((prevPending) =>
      prevPending.map((user) =>
        user.grantee_uuid === userId
          ? { ...user, privileges: [{ privilege_name: newPermission }] }
          : user
      )
    );
  }, []);

  const handleRemovePending = useCallback((userId) => {
    setPendingUsers((prevPending) => prevPending.filter((user) => user.grantee_uuid !== userId));
  }, []);

  const handleShareClick = useCallback(async () => {
    if (pendingUsers.length === 0) return;

    setIsLoadingSharing(true);
    const currentPendingUsers = [...pendingUsers];

    try {
      await handleAddMembers(currentPendingUsers);
      
      const userCount = currentPendingUsers.length;
      const userText = userCount === 1 ? 'user' : 'users';
      const verbText = userCount === 1 ? 'has' : 'have';
      toast.success({
        title: 'User added successfully',
        message: `${userCount} ${userText} ${verbText} been added successfully`,
      });
      
      setPendingUsers([]);
      setSelectedUsers([]);
      
      if (onShare) {
        onShare(currentPendingUsers);
      }
      
      // Close modal after successful operation
      onClose();
    } catch (error) {
      console.error('Error adding members to knowledge base:', error);
      toast.error({
        title: 'Error',
        message: error.message || 'An error occurred while adding members to knowledge base.',
      });
    } finally {
      setIsLoadingSharing(false);
    }
  }, [pendingUsers, handleAddMembers, toast, onShare, onClose]);

  const { isMobile, isIpadAirPortrait, isIpadProPortrait } = useMediaQueries();
  
  const knowledgeBaseName = useMemo(() => {
    return knowledgeBase?.name || 'Sales Config';
  }, [knowledgeBase?.name]);

  const modalWidth = useMemo(() => {
    if (isMobile || isIpadAirPortrait || isIpadProPortrait) {
      return '95%';
    }
    return '900px';
  }, [isMobile, isIpadAirPortrait, isIpadProPortrait]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="medium" width={modalWidth}>
      <Modal.Header>
        <Modal.Header.Title>
          Add Users
        </Modal.Header.Title>
      </Modal.Header>
      <Modal.Body>
        <ModalContent>
          <ShareKnowledgeModalContent
            knowledgeBaseName={knowledgeBaseName}
            selectedUsers={selectedUsers}
            onUsersChange={handleUsersChange}
            usersList={usersList}
          />

          <PendingUsersList
            pendingUsers={pendingUsers}
            privileges={MOCK_PRIVILEGES}
            onUpdatePermission={handleUpdatePendingPermission}
            onRemove={handleRemovePending}
          />

          <CurrentUsersList
            shouldShow={currentUsers.length > 0 || membersLoading}
            currentUsers={currentUsers}
            isLoading={membersLoading}
            privileges={MOCK_PRIVILEGES}
            onUpdatePermission={handleUpdateCurrentPermission}
            onRemove={handleRemoveCurrent}
          />
        </ModalContent>
      </Modal.Body>
      <Modal.Actions align="end">
        {pendingUsers.length > 0 && (
          <Button
            label="Add"
            size="medium"
            type="primary"
            onClick={handleShareClick}
            disabled={isLoadingSharing}
          />
        )}
      </Modal.Actions>
    </Modal>
  );
});

export default ShareKnowledgeModalStandalone;

